import React, {createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {useDispatch, useStore} from 'react-redux';
import {sendMessage} from '../../../actions';
import {MESSAGE_INFO} from '../../../constants';
import * as Async from '../../../features/async';
import {nomeArquivoDaResposta, salvarArquivoResposta} from '../../../utils/download';
import {mensagemDoErro} from '../../../utils/erro';
import {
    MENSAGEM_ERRO_PADRAO,
    SITUACAO_DOWNLOAD,
    ativo,
    progressoDoEvento,
    textoAviso
} from './downloadSegundoPlano';
import PainelDownloadsSegundoPlano from './PainelDownloadsSegundoPlano';

export const DownloadSegundoPlanoContext = createContext({iniciar: () => null});

/*
 * Estado e execução dos downloads em segundo plano: a requisição segue ao trocar de
 * tela e um painel único mostra a situação de cada arquivo. Os downloads entram numa
 * fila (FIFO) e só limiteSimultaneos rodam ao mesmo tempo; os demais ficam "Na fila".
 * O Blob não entra no state; fica em respostasRef até o usuário baixar ou remover.
 */
function DownloadSegundoPlanoProvider({children, limiteSimultaneos}) {
    const dispatch = useDispatch();
    const store = useStore();
    const [downloads, setDownloads] = useState([]);

    const requisicoesRef = useRef(new Map());
    const respostasRef = useRef(new Map());
    const tentativasRef = useRef(new Map());
    const filaRef = useRef([]);
    const ativosRef = useRef(new Set());
    const executarRef = useRef(() => {});
    const montadoRef = useRef(true);
    const proximoIdRef = useRef(1);
    const downloadsRef = useRef(downloads);
    downloadsRef.current = downloads;
    const limiteRef = useRef(1);
    limiteRef.current = Math.max(1, Number(limiteSimultaneos) || 1);

    const atualizar = useCallback((id, campos) => {
        setDownloads(atuais => atuais.map(item => item.id === id ? {...item, ...campos} : item));
    }, []);

    const remover = useCallback(id => {
        requisicoesRef.current.delete(id);
        respostasRef.current.delete(id);
        tentativasRef.current.delete(id);
        filaRef.current = filaRef.current.filter(item => item !== id);
        ativosRef.current.delete(id);
        setDownloads(atuais => atuais.filter(item => item.id !== id));
    }, []);

    // Inicia os próximos da fila enquanto houver vaga.
    const processarFila = useCallback(() => {
        while (ativosRef.current.size < limiteRef.current && filaRef.current.length > 0) {
            const id = filaRef.current.shift();
            if (requisicoesRef.current.has(id)) {
                ativosRef.current.add(id);
                atualizar(id, {situacao: SITUACAO_DOWNLOAD.GERANDO, progresso: null, mensagemErro: null});
                executarRef.current(id);
            }
        }
    }, [atualizar]);

    // Libera a vaga de um download ativo (uma única vez) e chama o próximo da fila.
    const liberar = useCallback(id => {
        if (ativosRef.current.delete(id)) {
            processarFila();
        }
    }, [processarFila]);

    const executar = useCallback(id => {
        const requisicao = requisicoesRef.current.get(id);
        if (!requisicao) {
            return;
        }

        const anterior = tentativasRef.current.get(id);
        const numero = (anterior ? anterior.numero : 0) + 1;
        const source = axios.CancelToken.source();
        const tentativa = {numero, source, ultimoProgresso: undefined};
        tentativasRef.current.set(id, tentativa);

        // Só vale se o provider segue montado e esta ainda é a tentativa atual do download.
        const vale = () => {
            const atual = tentativasRef.current.get(id);
            return montadoRef.current && atual?.numero === numero;
        };

        const credencial = store.getState().credencial;
        const requisitar = requisicao.metodo === 'GET' ? Async.fetchFile : Async.createFile;

        requisitar(requisicao.url, credencial, requisicao.data, {
            cancelToken: source.token,
            onDownloadProgress: evento => {
                if (!vale()) {
                    return;
                }
                const progresso = progressoDoEvento(evento);
                if (progresso !== tentativa.ultimoProgresso) {
                    tentativa.ultimoProgresso = progresso;
                    atualizar(id, {situacao: SITUACAO_DOWNLOAD.BAIXANDO, progresso});
                }
            }
        }).then(response => {
            if (!vale()) {
                return;
            }
            respostasRef.current.set(id, response);
            atualizar(id, {
                situacao: SITUACAO_DOWNLOAD.CONCLUIDO,
                progresso: 100,
                nomeArquivo: nomeArquivoDaResposta(response.headers, requisicao.nomeArquivo)
            });
            try {
                salvarArquivoResposta(response, requisicao.nomeArquivo);
            } finally {
                liberar(id);
            }
        }, erro => {
            if (!vale()) {
                return;
            }
            atualizar(id, {
                situacao: SITUACAO_DOWNLOAD.ERRO,
                mensagemErro: mensagemDoErro(erro, MENSAGEM_ERRO_PADRAO)
            });
            liberar(id);
        });
    }, [store, atualizar, liberar]);
    executarRef.current = executar;

    // Um aumento do limite abre vagas para quem está na fila.
    useEffect(() => {
        processarFila();
    }, [limiteSimultaneos]); // eslint-disable-line react-hooks/exhaustive-deps

    const iniciar = useCallback(({url, data = {}, metodo = 'POST', nomeArquivo, titulo, descricao = null, aviso = true}) => {
        const id = proximoIdRef.current++;
        const metodoNormalizado = String(metodo).toUpperCase() === 'GET' ? 'GET' : 'POST';

        requisicoesRef.current.set(id, {url, data, metodo: metodoNormalizado, nomeArquivo});
        setDownloads(atuais => [...atuais, {
            id,
            titulo,
            descricao,
            nomeArquivo,
            situacao: SITUACAO_DOWNLOAD.NA_FILA,
            progresso: null,
            mensagemErro: null
        }]);

        filaRef.current.push(id);
        processarFila();
        const naFila = filaRef.current.includes(id);

        if (aviso) {
            dispatch(sendMessage(MESSAGE_INFO, typeof aviso === 'string' ? aviso : textoAviso(titulo, naFila)));
        }

        return id;
    }, [dispatch, processarFila]);

    const tentarNovamente = useCallback(id => {
        const item = downloadsRef.current.find(download => download.id === id);
        if (!item || item.situacao !== SITUACAO_DOWNLOAD.ERRO
            || filaRef.current.includes(id) || ativosRef.current.has(id)) {
            return;
        }
        atualizar(id, {situacao: SITUACAO_DOWNLOAD.NA_FILA, progresso: null, mensagemErro: null});
        filaRef.current.push(id);
        processarFila();
    }, [atualizar, processarFila]);

    const cancelar = useCallback(id => {
        const estavaAtivo = ativosRef.current.has(id);
        const tentativa = tentativasRef.current.get(id);
        if (tentativa) {
            tentativa.source.cancel();
        }
        remover(id);
        if (estavaAtivo) {
            processarFila();
        }
    }, [remover, processarFila]);

    // Remove os itens que não estão na fila nem em andamento (concluídos e com erro).
    const limpar = useCallback(() => {
        downloadsRef.current
            .filter(item => !ativo(item.situacao))
            .forEach(item => remover(item.id));
    }, [remover]);

    const baixar = useCallback(id => {
        const resposta = respostasRef.current.get(id);
        const item = downloadsRef.current.find(download => download.id === id);
        if (resposta && item) {
            salvarArquivoResposta(resposta, item.nomeArquivo);
        }
    }, []);

    // Ao desmontar, cancela o que estiver em andamento e descarta fila e arquivos guardados.
    useEffect(() => {
        montadoRef.current = true;
        const tentativas = tentativasRef.current;
        const requisicoes = requisicoesRef.current;
        const respostas = respostasRef.current;
        const ativos = ativosRef.current;

        return () => {
            montadoRef.current = false;
            tentativas.forEach(tentativa => tentativa.source.cancel());
            tentativas.clear();
            requisicoes.clear();
            respostas.clear();
            ativos.clear();
            filaRef.current = [];
        };
    }, []);

    const api = useMemo(() => ({iniciar}), [iniciar]);

    return (
        <DownloadSegundoPlanoContext.Provider value={api}>
            {children}
            <PainelDownloadsSegundoPlano
                downloads={downloads}
                onBaixar={baixar}
                onTentarNovamente={tentarNovamente}
                onCancelar={cancelar}
                onRemover={remover}
                onLimpar={limpar} />
        </DownloadSegundoPlanoContext.Provider>
    );
}

DownloadSegundoPlanoProvider.propTypes = {
    children: PropTypes.node,
    limiteSimultaneos: PropTypes.number
};

DownloadSegundoPlanoProvider.defaultProps = {
    limiteSimultaneos: 1
};

export default DownloadSegundoPlanoProvider;
